/**
 * DAG Validator — Validates the Learning Dependency Graph structure.
 *
 * ARCHITECTURE v4.0 — Phase 1, Step 1.7
 *
 * Validates:
 * 1. No cycles (Kahn's algorithm)
 * 2. All prerequisite references exist in taxonomy
 * 3. No self-references
 * 4. Tier monotonicity (prerequisites must have lower or equal tier)
 * 5. No cross-module prerequisites (within same module only, with exceptions)
 * 6. Connected component analysis
 */

import type { DAGValidationResult, DAGValidationError, PrerequisiteDefinition } from './types';
import { FAMILY_BY_ID, type FamilyDefinition, getGroupForTier } from '../../content';

/**
 * Validate the DAG formed by prerequisite definitions.
 *
 * This is the primary validation entry point. It runs all checks
 * and returns a comprehensive result.
 */
export function validateDAG(prerequisites: PrerequisiteDefinition[]): DAGValidationResult {
  const errors: DAGValidationError[] = [];
  const warnings: string[] = [];

  // Build adjacency structures
  const adjList = new Map<string, string[]>(); // familyId → prerequisites
  const reverseAdj = new Map<string, string[]>(); // familyId → dependents
  const allFamilyIds = new Set<string>(FAMILY_BY_ID.keys());

  // Initialize all nodes
  for (const familyId of allFamilyIds) {
    adjList.set(familyId, []);
    reverseAdj.set(familyId, []);
  }

  // Populate from prerequisite definitions
  for (const def of prerequisites) {
    // Check family exists
    if (!allFamilyIds.has(def.familyId)) {
      errors.push({
        type: 'UNKNOWN_PREREQUISITE',
        message: `Family "${def.familyId}" in prerequisite definition not found in taxonomy`,
        familyIds: [def.familyId]
      });
      continue;
    }
    for (const prereq of def.prerequisites) {
      // Self-reference check
      if (prereq === def.familyId) {
        errors.push({
          type: 'SELF_REFERENCE',
          message: `Family "${def.familyId}" lists itself as a prerequisite`,
          familyIds: [def.familyId]
        });
        continue;
      }

      // Prerequisite exists check
      if (!allFamilyIds.has(prereq)) {
        errors.push({
          type: 'UNKNOWN_PREREQUISITE',
          message: `Prerequisite "${prereq}" for family "${def.familyId}" not found in taxonomy`,
          familyIds: [def.familyId, prereq]
        });
        continue;
      }

      // Tier monotonicity check
      const familyDef = FAMILY_BY_ID.get(def.familyId);
      const prereqDef = FAMILY_BY_ID.get(prereq);
      if (!familyDef || !prereqDef) {
        errors.push({
          type: 'UNKNOWN_PREREQUISITE',
          message: `FAMILY_BY_ID lookup failed for "${!familyDef ? def.familyId : prereq}" — taxonomy/allFamilyIds mismatch`,
          familyIds: [def.familyId, prereq]
        });
        continue;
      }
      if (prereqDef.tier > familyDef.tier) {
        errors.push({
          type: 'TIER_MONOTONICITY_VIOLATION',
          message: `Prerequisite "${prereq}" (tier ${prereqDef.tier}) has higher tier than "${def.familyId}" (tier ${familyDef.tier})`,
          familyIds: [def.familyId, prereq]
        });
      }

      // Cross-module check (warning, not error — some cross-module deps are valid)
      if (familyDef.module !== prereqDef.module) {
        warnings.push(`Cross-module prerequisite: "${prereq}" (${prereqDef.module}) → "${def.familyId}" (${familyDef.module}). Verify this is intentional.`);
      }

      // B4: Cross-group prerequisite check (warning — QF-24 prevention)
      const familyGroup = getGroupForTier(familyDef.tier);
      const prereqGroup = getGroupForTier(prereqDef.tier);
      if (familyGroup && prereqGroup && familyGroup !== prereqGroup) {
        warnings.push(`Cross-group prerequisite: "${prereq}" (group ${prereqGroup}, tier ${prereqDef.tier}) → "${def.familyId}" (group ${familyGroup}, tier ${familyDef.tier}). ` + `Ensure arePrerequisitesMet() auto-pass handles this (QF-24 pattern).`);
      }

      // Add edge
      const existing = adjList.get(def.familyId) ?? [];
      existing.push(prereq);
      adjList.set(def.familyId, existing);
      const reverseExisting = reverseAdj.get(prereq) ?? [];
      reverseExisting.push(def.familyId);
      reverseAdj.set(prereq, reverseExisting);
    }
  }

  // Cycle detection using Kahn's algorithm
  const cycleResult = detectCycles(adjList, allFamilyIds);
  if (!cycleResult.isDAG) {
    for (const cycle of cycleResult.cycles) {
      errors.push({
        type: 'CYCLE_DETECTED',
        message: `Cycle detected: ${cycle.join(' → ')}`,
        familyIds: cycle,
        cyclePath: cycle
      });
    }
  }

  // Compute depths
  const depths = computeDepths(adjList, allFamilyIds);
  const maxDepth = Math.max(...Array.from(depths.values()), 0);
  const avgDepth = depths.size > 0 ? Array.from(depths.values()).reduce((a, b) => a + b, 0) / depths.size : 0;

  // Find roots and leaves
  const roots = Array.from(allFamilyIds).filter(id => (adjList.get(id) ?? []).length === 0);
  const leaves = Array.from(allFamilyIds).filter(id => (reverseAdj.get(id) ?? []).length === 0);

  // Connected components
  const componentCount = countConnectedComponents(adjList, reverseAdj, allFamilyIds);

  // Count edges
  let edgeCount = 0;
  for (const prereqs of adjList.values()) {
    edgeCount += prereqs.length;
  }
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    stats: {
      nodeCount: allFamilyIds.size,
      edgeCount,
      rootCount: roots.length,
      leafCount: leaves.length,
      maxDepth,
      avgDepth: Math.round(avgDepth * 10) / 10,
      componentCount
    }
  };
}

// ─── Kahn's Algorithm for Cycle Detection ─────────────────────────────────────

interface CycleDetectionResult {
  isDAG: boolean;
  topologicalOrder: string[];
  cycles: string[][];
}
function detectCycles(adjList: Map<string, string[]>, allNodes: Set<string>): CycleDetectionResult {
  // Compute in-degrees (number of prerequisites each node has)
  const inDegree = new Map<string, number>();
  for (const node of allNodes) {
    inDegree.set(node, 0);
  }

  // In our adjList, adjList.get(X) = prerequisites of X
  // So for each X → prereq relationship, prereq has an outgoing edge to X
  // In-degree of X = number of prerequisites X has
  for (const [node, prereqs] of adjList) {
    inDegree.set(node, prereqs.length);
  }

  // Queue: start with nodes that have no prerequisites (in-degree 0)
  const queue: string[] = [];
  for (const [node, degree] of inDegree) {
    if (degree === 0) {
      queue.push(node);
    }
  }
  const topologicalOrder: string[] = [];
  const processed = new Set<string>();
  while (queue.length > 0) {
    const node = queue.shift()!;
    topologicalOrder.push(node);
    processed.add(node);

    // For each family that depends on this node, reduce their in-degree
    for (const [dependent, prereqs] of adjList) {
      if (prereqs.includes(node)) {
        const newDegree = (inDegree.get(dependent) ?? 0) - 1;
        inDegree.set(dependent, newDegree);
        if (newDegree === 0) {
          queue.push(dependent);
        }
      }
    }
  }
  const isDAG = processed.size === allNodes.size;

  // Find cycles if not a DAG
  const cycles: string[][] = [];
  if (!isDAG) {
    const unprocessed = new Set(Array.from(allNodes).filter(n => !processed.has(n)));
    // Use DFS to find cycle paths
    const visited = new Set<string>();
    const inStack = new Set<string>();
    function dfs(node: string, path: string[]): boolean {
      if (inStack.has(node)) {
        // Found a cycle
        const cycleStart = path.indexOf(node);
        cycles.push([...path.slice(cycleStart), node]);
        return true;
      }
      if (visited.has(node)) return false;
      visited.add(node);
      inStack.add(node);
      path.push(node);
      for (const prereq of adjList.get(node) ?? []) {
        if (unprocessed.has(prereq)) {
          dfs(prereq, path);
        }
      }
      path.pop();
      inStack.delete(node);
      return false;
    }
    for (const node of unprocessed) {
      if (!visited.has(node)) {
        dfs(node, []);
      }
    }
  }
  return {
    isDAG,
    topologicalOrder,
    cycles
  };
}

// ─── Depth Computation ────────────────────────────────────────────────────────

function computeDepths(adjList: Map<string, string[]>, allNodes: Set<string>): Map<string, number> {
  const depths = new Map<string, number>();
  const computed = new Set<string>();
  function getDepth(node: string): number {
    if (computed.has(node)) return depths.get(node) ?? 0;
    const prereqs = adjList.get(node) ?? [];
    if (prereqs.length === 0) {
      depths.set(node, 0);
      computed.add(node);
      return 0;
    }

    // Guard against cycles (return 0 if we're in a cycle)
    depths.set(node, 0);
    computed.add(node);
    const maxPrereqDepth = Math.max(...prereqs.map(p => getDepth(p)));
    const depth = maxPrereqDepth + 1;
    depths.set(node, depth);
    return depth;
  }
  for (const node of allNodes) {
    getDepth(node);
  }
  return depths;
}

// ─── Connected Components ─────────────────────────────────────────────────────

function countConnectedComponents(adjList: Map<string, string[]>, reverseAdj: Map<string, string[]>, allNodes: Set<string>): number {
  const visited = new Set<string>();
  let components = 0;
  function bfs(start: string) {
    const queue = [start];
    visited.add(start);
    while (queue.length > 0) {
      const node = queue.shift()!;

      // Visit prerequisites (undirected for component counting)
      for (const prereq of adjList.get(node) ?? []) {
        if (!visited.has(prereq)) {
          visited.add(prereq);
          queue.push(prereq);
        }
      }

      // Visit dependents
      for (const dep of reverseAdj.get(node) ?? []) {
        if (!visited.has(dep)) {
          visited.add(dep);
          queue.push(dep);
        }
      }
    }
  }
  for (const node of allNodes) {
    if (!visited.has(node)) {
      bfs(node);
      components++;
    }
  }
  return components;
}